import { ReactElement } from 'react';
import { UIText } from '@/ui';

/** The minimal shape a status column needs: an on/off flag and its label. */
export type StatusSide = { enabled: boolean; status: string };

/**
 * A status column: colour-coded on/off, showing a muted "old → new" when the
 * enabled state changes between the current and pending sides. Shared by the
 * geofence and Wi-Fi tables to keep the pending transition language consistent.
 */
export function StatusCell(props: {
    current: StatusSide;
    pending?: StatusSide;
    /** Render with the small text variant (e.g. inside the devices-list popover). */
    small?: boolean;
}): ReactElement {
    const { current, pending, small } = props;
    const variant = small ? 'small' : 'basic';
    if (!pending || pending.enabled === current.enabled) {
        return (
            <UIText
                variant={variant}
                medium
                type={current.enabled ? 'link' : 'error'}
            >
                {current.status}
            </UIText>
        );
    }
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <UIText variant={variant} medium type="disabled">
                {current.status}
            </UIText>
            <UIText variant={variant} type="default2">
                →
            </UIText>
            <UIText
                variant={variant}
                medium
                type={pending.enabled ? 'link' : 'error'}
            >
                {pending.status}
            </UIText>
        </span>
    );
}

/**
 * A neutral detail column: shows the current value, or a muted "old → new" when
 * a pending value is supplied and differs.
 */
export function DiffCell(props: {
    current: string;
    pending?: string;
}): ReactElement {
    const { current, pending } = props;
    if (pending === undefined || pending === current) {
        return <UIText type="default2">{current}</UIText>;
    }
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <UIText type="disabled">{current}</UIText>
            <UIText type="default2">→</UIText>
            <UIText type="default2">{pending}</UIText>
        </span>
    );
}
